import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import CoverUploader from "@/components/CoverUploader";
import VoiceCapture from "@/components/VoiceCapture";

const GENRES = ["Literary Fiction", "Mystery", "Sci-Fi", "Fantasy", "Poetry", "Essay", "Memoir", "Screenplay", "Other"];
const TYPES = [
  { v: "novel", l: "Novel" }, { v: "screenplay", l: "Screenplay" }, { v: "short_story", l: "Short story" },
  { v: "poem", l: "Poem" }, { v: "essay", l: "Essay" }, { v: "other", l: "Other" },
];

const DRAFT_KEY = "quill_upload_draft";
const DEFAULT_DRAFT = {
  title: "", genre: "Literary Fiction", work_type: "novel",
  excerpt: "", full_content: "", price_dollars: "5.00", completion_percent: 50,
  cover_path: null,
};

function readLocalDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "form" in parsed) return parsed;
    return { form: { ...DEFAULT_DRAFT, ...parsed }, updated_at: null };
  } catch { return null; }
}

function isDraftMeaningful(d) {
  return !!(d && (d.title || d.excerpt || d.full_content || d.cover_path));
}

const inputCls = "w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-2.5 text-[#F7F3EC] font-mono-brass text-sm";

function Field({ label, children, action }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">{label}</label>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function UploadModal({ onClose, onCreated }) {
  const [form, setForm] = useState(() => {
    const local = readLocalDraft();
    return local?.form ? { ...DEFAULT_DRAFT, ...local.form } : { ...DEFAULT_DRAFT };
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [syncState, setSyncState] = useState("idle");
  const [hydrated, setHydrated] = useState(false);
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    let mounted = true;
    const local = readLocalDraft();
    api.get("/drafts").then(({ data }) => {
      if (!mounted) return;
      const remote = isDraftMeaningful(data) ? { form: { ...DEFAULT_DRAFT, ...data }, updated_at: data.updated_at } : null;
      const localHas = isDraftMeaningful(local?.form);
      if (remote && localHas) {
        const localT = local.updated_at ? new Date(local.updated_at).getTime() : 0;
        const remoteT = remote.updated_at ? new Date(remote.updated_at).getTime() : 0;
        const differs = JSON.stringify(local.form) !== JSON.stringify(remote.form);
        if (differs && remoteT > localT) {
          setConflict({ local, remote });
          setSyncState("synced");
          return;
        }
        if (remoteT > localT) setForm(remote.form);
        setSyncState("synced");
      } else if (remote) {
        setForm(remote.form);
        setSyncState("synced");
      }
    }).catch(() => setSyncState("offline")).finally(() => setHydrated(true));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!hydrated || conflict) return;
    const t = setTimeout(async () => {
      const now = new Date().toISOString();
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, updated_at: now })); } catch (err) { console.error("Local draft cache failed", err); }
      setSyncState("syncing");
      try {
        await api.put("/drafts", form);
        setSavedAt(new Date());
        setSyncState("synced");
      } catch (err) {
        console.error("Draft sync failed", err);
        setSyncState("offline");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form, hydrated, conflict]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const discardDraft = async () => {
    if (!window.confirm("Discard this draft? It will be cleared from every device.")) return;
    try { await api.delete("/drafts"); } catch (err) { console.error("Server draft delete failed", err); }
    localStorage.removeItem(DRAFT_KEY);
    setForm({ ...DEFAULT_DRAFT });
    setSavedAt(null);
    setSyncState("idle");
    toast.success("Draft discarded on all devices.");
  };

  const resolveConflict = (which) => {
    const chosen = which === "remote" ? conflict.remote.form : conflict.local.form;
    setForm(chosen);
    setConflict(null);
    toast.success(which === "remote" ? "Restored draft from your other device." : "Kept this device's draft.");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (conflict) { toast.error("Choose which draft to keep first."); return; }
    setSaving(true);
    try {
      const resp = await api.post("/works", {
        title: form.title,
        genre: form.genre,
        work_type: form.work_type,
        excerpt: form.excerpt,
        full_content: form.full_content,
        price_cents: Math.round(parseFloat(form.price_dollars) * 100),
        completion_percent: parseInt(form.completion_percent, 10),
        cover_path: form.cover_path,
      });

      if (resp.status === 202 && resp.data.job_id) {
        toast.info("Long manuscript — running content review. This can take up to a minute.", { duration: 6000 });
        try { await api.delete("/drafts"); } catch (err) { console.error("Server draft cleanup failed", err); }
        localStorage.removeItem(DRAFT_KEY);
        const jobId = resp.data.job_id;
        const started = Date.now();
        while (Date.now() - started < 120000) {
          await new Promise((r) => setTimeout(r, 2500));
          try {
            const { data } = await api.get(`/works/moderation-jobs/${jobId}`);
            if (data.status === "approved") { toast.success("Content review passed — fragment listed for sale."); onCreated(); return; }
            if (data.status === "rejected") { toast.error(`Rejected by content policy: ${data.reason || "policy violation"}`); setSaving(false); return; }
            if (data.status === "error") { toast.error("Content review failed. Please try again."); setSaving(false); return; }
          } catch (err) { console.error("Job poll failed", err); }
        }
        toast.error("Content review is taking longer than expected — check your dashboard shortly.");
        onCreated();
        return;
      }

      try { await api.delete("/drafts"); } catch (err) { console.error("Server draft cleanup failed", err); }
      localStorage.removeItem(DRAFT_KEY);
      toast.success("Fragment listed for sale.");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const syncLabel =
    conflict ? "Two drafts detected — pick one to continue" :
    syncState === "syncing" ? "Syncing draft to cloud…" :
    syncState === "synced" && savedAt ? `Synced across devices · ${savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` :
    syncState === "offline" ? "Offline — draft saved locally only" :
    "Draft syncs to the cloud as you type";
  const syncColor =
    conflict ? "text-[#E6B860]" :
    syncState === "offline" ? "text-[#8C3A34]" :
    syncState === "syncing" ? "text-[#A8A095]" :
    "text-[#D9B472]";

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose} data-testid="upload-modal">
      <div className="relative w-full max-w-2xl bg-[#161412] border border-[rgba(217,180,114,0.25)] rounded-sm p-6 sm:p-8 my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6E675E] hover:text-[#F7F3EC]" data-testid="upload-close"><X className="w-5 h-5" /></button>
        <div className="chip mb-3">List a fragment</div>
        <h3 className="font-serif-display text-3xl text-[#F7F3EC] mb-2">Sell your unfinished work</h3>
        <div className="flex items-center justify-between mb-6">
          <span className={`text-[10px] uppercase tracking-widest font-mono-brass ${syncColor}`} data-testid="draft-status">{syncLabel}</span>
          <button type="button" onClick={discardDraft} className="text-[10px] uppercase tracking-widest text-[#8C3A34] hover:text-[#E9B8A8] font-mono-brass transition-colors duration-200" data-testid="draft-discard">Discard draft</button>
        </div>

        {conflict && (
          <div className="mb-6 border border-[rgba(230,184,96,0.4)] rounded-sm p-5 bg-[rgba(217,180,114,0.05)]" data-testid="draft-conflict">
            <div className="text-[10px] uppercase tracking-widest text-[#E6B860] font-mono-brass mb-2">Two drafts on file</div>
            <h4 className="font-serif-display text-xl text-[#F7F3EC] mb-2">Keep the version from your other device?</h4>
            <p className="text-sm text-[#A8A095] font-serif-body italic mb-4">A newer draft was saved somewhere else. Pick which one to continue with — the other will be replaced.</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <div className="border border-[rgba(217,180,114,0.2)] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass mb-1">Other device (newer)</div>
                <div className="font-serif-display text-[#F7F3EC] truncate">{conflict.remote.form.title || <span className="italic text-[#6E675E]">Untitled</span>}</div>
                <div className="text-xs text-[#6E675E] font-mono-brass mt-1">{conflict.remote.updated_at ? new Date(conflict.remote.updated_at).toLocaleString() : "—"}</div>
                <div className="text-xs text-[#A8A095] font-serif-body mt-2 line-clamp-2 italic">{(conflict.remote.form.excerpt || "").slice(0, 120) || "no excerpt yet"}</div>
              </div>
              <div className="border border-[rgba(217,180,114,0.2)] rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-1">This device</div>
                <div className="font-serif-display text-[#F7F3EC] truncate">{conflict.local.form.title || <span className="italic text-[#6E675E]">Untitled</span>}</div>
                <div className="text-xs text-[#6E675E] font-mono-brass mt-1">{conflict.local.updated_at ? new Date(conflict.local.updated_at).toLocaleString() : "—"}</div>
                <div className="text-xs text-[#A8A095] font-serif-body mt-2 line-clamp-2 italic">{(conflict.local.form.excerpt || "").slice(0, 120) || "no excerpt yet"}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => resolveConflict("remote")} className="btn-buy px-5 py-2.5 rounded-sm font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="draft-keep-remote">Use other device</button>
              <button type="button" onClick={() => resolveConflict("local")} className="btn-ghost-gold px-5 py-2.5 rounded-sm font-mono-brass text-xs uppercase tracking-widest" data-testid="draft-keep-local">Keep this device</button>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <CoverUploader value={form.cover_path} onChange={(p) => setForm({ ...form, cover_path: p })} />
          <Field label="Title"><input required value={form.title} onChange={upd("title")} className={inputCls} data-testid="upload-title" /></Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Genre">
              <select value={form.genre} onChange={upd("genre")} className={inputCls} data-testid="upload-genre">
                {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Form">
              <select value={form.work_type} onChange={upd("work_type")} className={inputCls} data-testid="upload-type">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (USD)"><input required type="number" min="1" step="0.01" value={form.price_dollars} onChange={upd("price_dollars")} className={inputCls} data-testid="upload-price" /></Field>
            <Field label="Draft complete (%)"><input required type="number" min="0" max="99" value={form.completion_percent} onChange={upd("completion_percent")} className={inputCls} data-testid="upload-completion" /></Field>
          </div>

          <Field label="Free Excerpt (public)" action={<VoiceCapture testid="voice-excerpt" label="Dictate excerpt" onTranscribed={(t) => setForm(f => ({ ...f, excerpt: (f.excerpt ? f.excerpt + " " : "") + t }))} />}><textarea required rows={4} value={form.excerpt} onChange={upd("excerpt")} className={`${inputCls} font-serif-body`} data-testid="upload-excerpt" /></Field>
          <Field label="Full Manuscript (locked)" action={<VoiceCapture testid="voice-full" label="Dictate manuscript" onTranscribed={(t) => setForm(f => ({ ...f, full_content: (f.full_content ? f.full_content + " " : "") + t }))} />}><textarea required rows={6} value={form.full_content} onChange={upd("full_content")} className={`${inputCls} font-serif-body`} data-testid="upload-full" /></Field>

          <button type="submit" disabled={saving} className="btn-buy w-full rounded-sm py-3.5 font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="upload-submit">
            {saving ? "Listing…" : "List anonymously"}
          </button>
        </form>
      </div>
    </div>
  );
}
